import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from "recharts";
import { Badge } from "@/components/ui/badge";
import { PaginationControls, clampPage } from "@/components/pagination-controls";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { DrillUser, GeminiCostAlertsResponse, GeminiUsageResponse } from "./types";
import { OTHER_COLOR, TREND_COLORS } from "./constants";
import { downloadCsv, formatDate, formatNum, formatUsd, rangeSlug, rowsToCsv, userLabel } from "./helpers";
import { GeminiAlertSettingsCard } from "./GeminiAlertSettingsCard";
import { GeminiUserDrilldownDialog } from "./GeminiUserDrilldownDialog";

export function GeminiUsagePanel() {
  const queryClient = useQueryClient();
  const [hours, setHours] = useState<number>(24);
  const [topNMetric, setTopNMetric] = useState<"cost" | "totalTokens" | "calls">("cost");
  const [topN, setTopN] = useState<number>(10);
  const [trendMetric, setTrendMetric] = useState<"cost" | "totalTokens">("cost");
  const [trendTopN, setTrendTopN] = useState<number>(5);
  const [drillUser, setDrillUser] = useState<DrillUser | null>(null);
  const [drillAlertDate, setDrillAlertDate] = useState<string | null>(null);
  const [featureFilter, setFeatureFilter] = useState<string | null>(null);
  const [knownFeatures, setKnownFeatures] = useState<string[]>([]);
  const featureParam = featureFilter
    ? `&feature=${encodeURIComponent(featureFilter === "(unknown)" ? "__null__" : featureFilter)}`
    : "";
  const [showAcknowledged, setShowAcknowledged] = useState<boolean>(false);
  const [usageByFeaturePage, setUsageByFeaturePage] = useState(1);
  const [usageByFeaturePageSize, setUsageByFeaturePageSize] = useState(25);
  const [topUsersPage, setTopUsersPage] = useState(1);
  const [topUsersPageSize, setTopUsersPageSize] = useState(25);
  const [alertsPage, setAlertsPage] = useState(1);
  const [alertsPageSize, setAlertsPageSize] = useState(25);
  const [usageRecentPage, setUsageRecentPage] = useState(1);
  const [usageRecentPageSize, setUsageRecentPageSize] = useState(25);
  useEffect(() => {
    setUsageByFeaturePage(1);
    setTopUsersPage(1);
    setUsageRecentPage(1);
  }, [hours, featureFilter]);
  useEffect(() => {
    setTopUsersPage(1);
  }, [topNMetric, topN]);
  useEffect(() => {
    setAlertsPage(1);
  }, [showAcknowledged]);
  const { toast: geminiToast } = useToast();
  const usageQuery = useQuery<GeminiUsageResponse>({
    queryKey: ["/api/admin/gemini-usage", hours, featureFilter],
    queryFn: async () => {
      const res = await fetch(`/api/admin/gemini-usage?hours=${hours}${featureParam}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load Gemini usage");
      return res.json();
    },
  });
  // Keep a stable list of feature options derived from unfiltered fetches so
  // the dropdown doesn't collapse to just the current filter.
  useEffect(() => {
    if (featureFilter != null) return;
    const data = usageQuery.data;
    if (!data) return;
    const names = data.byFeature.map((r) => r.feature ?? "(unknown)");
    setKnownFeatures((prev) => {
      const merged = new Set<string>(prev);
      names.forEach((n) => merged.add(n));
      const next: string[] = [];
      merged.forEach((v) => next.push(v));
      next.sort((a, b) => a.localeCompare(b));
      if (next.length === prev.length && next.every((v, i) => v === prev[i])) return prev;
      return next;
    });
  }, [usageQuery.data, featureFilter]);
  const featureOptions = (() => {
    const set = new Set(knownFeatures);
    if (featureFilter) set.add(featureFilter);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  })();
  const alertsQuery = useQuery<GeminiCostAlertsResponse>({
    queryKey: ["/api/admin/gemini-cost-alerts", 25, showAcknowledged],
    queryFn: async () => {
      const res = await fetch(
        `/api/admin/gemini-cost-alerts?limit=25&includeAcknowledged=${showAcknowledged ? "true" : "false"}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error("Failed to load Gemini cost alerts");
      return res.json();
    },
  });

  const acknowledgeMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async ({ id, acknowledge }: { id: number; acknowledge: boolean }) => {
      const path = acknowledge ? "acknowledge" : "unacknowledge";
      return apiRequest("POST", `/api/admin/gemini-cost-alerts/${id}/${path}`);
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/gemini-cost-alerts"] });
      geminiToast({
        title: vars.acknowledge ? "Alert acknowledged" : "Alert reopened",
      });
    },
    onError: (err: any) => {
      geminiToast({
        title: "Action failed",
        description: parseErrorMessage(err, "Could not update alert."),
        variant: "destructive",
      });
    },
  });

  const ranges: Array<{ label: string; value: number }> = [
    { label: "Last 1 hour", value: 1 },
    { label: "Last 24 hours", value: 24 },
    { label: "Last 7 days", value: 24 * 7 },
    { label: "Last 30 days", value: 24 * 30 },
  ];

  const handleDownloadCsv = () => {
    const data = usageQuery.data;
    if (!data) return;
    const sections: Array<Array<unknown>> = [];
    const slug = rangeSlug(hours);
    const rangeLabel = ranges.find((r) => r.value === hours)?.label ?? `Last ${hours} hours`;

    sections.push(["Gemini API Usage Export"]);
    sections.push(["Time range", rangeLabel]);
    sections.push(["Feature filter", featureFilter ?? "All features"]);
    sections.push(["Generated at", new Date().toISOString()]);
    sections.push([]);

    sections.push(["Totals"]);
    sections.push(["Calls", "Successes", "Failures", "Input tokens", "Output tokens", "Total tokens", "Estimated cost (USD)"]);
    sections.push([
      data.totals.calls,
      data.totals.successes,
      data.totals.failures,
      data.totals.inputTokens,
      data.totals.outputTokens,
      data.totals.totalTokens,
      data.totals.estimatedCostUsd.toFixed(6),
    ]);
    sections.push([]);

    sections.push(["Usage by feature"]);
    sections.push(["Feature", "Model", "Calls", "Successes", "Failures", "Input tokens", "Output tokens", "Total tokens", "Avg latency (ms)", "Estimated cost (USD)"]);
    for (const r of data.byFeature) {
      sections.push([
        r.feature, r.model, r.calls, r.successes, r.failures,
        r.inputTokens, r.outputTokens, r.totalTokens, r.avgLatencyMs, r.estimatedCostUsd.toFixed(6),
      ]);
    }
    sections.push([]);

    const metricLabel = topNMetric === "cost" ? "Est. cost" : topNMetric === "totalTokens" ? "Total tokens" : "Calls";
    sections.push([`Top ${topN} users (ranked by ${metricLabel})`]);
    sections.push(["Rank", "User ID", "Email", "Display name", "Calls", "Successes", "Failures", "Input tokens", "Output tokens", "Total tokens", "Estimated cost (USD)"]);
    const sortedUsers = [...(data.byUser ?? [])].sort((a, b) => {
      if (topNMetric === "cost") return b.estimatedCostUsd - a.estimatedCostUsd;
      if (topNMetric === "totalTokens") return b.totalTokens - a.totalTokens;
      return b.calls - a.calls;
    }).slice(0, topN);
    let userRank = 0;
    for (const r of sortedUsers) {
      userRank += 1;
      sections.push([
        userRank, r.userId ?? "", r.email ?? "", r.displayName ?? "",
        r.calls, r.successes, r.failures,
        r.inputTokens, r.outputTokens, r.totalTokens, r.estimatedCostUsd.toFixed(6),
      ]);
    }
    sections.push([]);

    sections.push(["Recent calls"]);
    sections.push(["When (ISO)", "Feature", "Model", "User ID", "Email", "Display name", "Input tokens", "Output tokens", "Total tokens", "Latency (ms)", "Success", "Error message", "Estimated cost (USD)"]);
    for (const r of data.recent) {
      sections.push([
        r.createdAt, r.feature, r.model,
        r.userId ?? "", r.userEmail ?? "", r.userDisplayName ?? "",
        r.inputTokens, r.outputTokens, r.totalTokens, r.latencyMs,
        r.success ? "true" : "false", r.errorMessage ?? "",
        r.estimatedCostUsd.toFixed(6),
      ]);
    }

    const featureSlug = featureFilter
      ? `-${featureFilter.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "feature"}`
      : "";
    downloadCsv(`gemini-usage-${slug}${featureSlug}.csv`, rowsToCsv(sections));
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold" data-testid="text-gemini-usage-title">Gemini API Usage</h3>
            <p className="text-sm text-muted-foreground">
              Per-feature usage and estimated cost for the GEMINI_API_KEY secret. Cost estimates use published per-million-token list prices and may differ slightly from the Google Cloud bill.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Label htmlFor="select-gemini-range" className="text-sm">Time range</Label>
            <Select value={String(hours)} onValueChange={(v) => setHours(parseInt(v, 10))}>
              <SelectTrigger id="select-gemini-range" className="w-[180px]" data-testid="select-gemini-range">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ranges.map((r) => (
                  <SelectItem key={r.value} value={String(r.value)}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Label htmlFor="select-gemini-feature" className="text-sm">Feature</Label>
            <Select
              value={featureFilter ?? "__all__"}
              onValueChange={(v) => setFeatureFilter(v === "__all__" ? null : v)}
            >
              <SelectTrigger id="select-gemini-feature" className="w-[200px]" data-testid="select-gemini-feature">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All features</SelectItem>
                {featureOptions.map((f) => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {featureFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFeatureFilter(null)}
                data-testid="button-clear-gemini-feature"
              >
                Clear feature
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => usageQuery.refetch()}
              data-testid="button-refresh-gemini-usage"
            >
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadCsv}
              disabled={!usageQuery.data}
              data-testid="button-download-gemini-csv"
            >
              <Download className="h-4 w-4 mr-1" />
              Download CSV
            </Button>
          </div>
        </div>
      </Card>

      {usageQuery.isLoading ? (
        <Card className="p-6 space-y-3">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
        </Card>
      ) : usageQuery.isError ? (
        <Card className="p-6 text-sm text-destructive" data-testid="text-gemini-usage-error">
          Failed to load Gemini usage. Please try again.
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="text-xs text-muted-foreground">Total calls</div>
              <div className="text-2xl font-semibold mt-1" data-testid="text-gemini-total-calls">
                {formatNum(usageQuery.data!.totals.calls)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {formatNum(usageQuery.data!.totals.successes)} ok · {formatNum(usageQuery.data!.totals.failures)} failed
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-xs text-muted-foreground">Input tokens</div>
              <div className="text-2xl font-semibold mt-1" data-testid="text-gemini-input-tokens">
                {formatNum(usageQuery.data!.totals.inputTokens)}
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-xs text-muted-foreground">Output tokens</div>
              <div className="text-2xl font-semibold mt-1" data-testid="text-gemini-output-tokens">
                {formatNum(usageQuery.data!.totals.outputTokens)}
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-xs text-muted-foreground">Estimated cost</div>
              <div className="text-2xl font-semibold mt-1" data-testid="text-gemini-total-cost">
                {formatUsd(usageQuery.data!.totals.estimatedCostUsd)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {formatNum(usageQuery.data!.totals.totalTokens)} total tokens
              </div>
            </Card>
          </div>

          {(() => {
            const series = usageQuery.data!.series;
            if (!series || series.points.length === 0) {
              return (
                <Card className="p-6 text-sm text-muted-foreground" data-testid="text-gemini-trend-empty">
                  No usage data to chart in this window.
                </Card>
              );
            }
            const valueOf = (p: { estimatedCostUsd: number; totalTokens: number }) =>
              trendMetric === "cost" ? p.estimatedCostUsd : p.totalTokens;
            const userTotals = new Map<string, { key: string; label: string; total: number }>();
            for (const p of series.points) {
              const key = p.userId == null ? "anon" : String(p.userId);
              const cur = userTotals.get(key) ?? { key, label: userLabel(p), total: 0 };
              cur.total += valueOf(p);
              userTotals.set(key, cur);
            }
            const ranked = Array.from(userTotals.values()).sort((a, b) => b.total - a.total);
            const topKeys = new Set(ranked.slice(0, trendTopN).map((u) => u.key));
            const hasOther = ranked.length > trendTopN;
            const seriesKeys: Array<{ key: string; label: string; color: string }> = ranked
              .slice(0, trendTopN)
              .map((u, i) => ({ key: u.key, label: u.label, color: TREND_COLORS[i % TREND_COLORS.length] }));
            if (hasOther) seriesKeys.push({ key: "__other__", label: "Other", color: OTHER_COLOR });

            const userMetaByKey = new Map<string, { userId: number | null; email: string | null; displayName: string | null }>();
            for (const p of series.points) {
              const key = p.userId == null ? "anon" : String(p.userId);
              if (!userMetaByKey.has(key)) {
                userMetaByKey.set(key, { userId: p.userId, email: p.email, displayName: p.displayName });
              }
            }
            const handleSeriesClick = (key: string) => {
              if (key === "__other__") return;
              const meta = userMetaByKey.get(key);
              if (meta) { setDrillUser(meta); setDrillAlertDate(null); }
            };

            const bucketMap = new Map<string, Record<string, number | string>>();
            for (const p of series.points) {
              const row = bucketMap.get(p.bucketStart) ?? { bucketStart: p.bucketStart };
              const key = p.userId == null ? "anon" : String(p.userId);
              const bucketKey = topKeys.has(key) ? key : "__other__";
              row[bucketKey] = (Number(row[bucketKey]) || 0) + valueOf(p);
              bucketMap.set(p.bucketStart, row);
            }
            const chartData = Array.from(bucketMap.values()).sort((a, b) =>
              String(a.bucketStart).localeCompare(String(b.bucketStart)),
            );
            for (const row of chartData) {
              for (const sk of seriesKeys) {
                if (row[sk.key] == null) row[sk.key] = 0;
              }
            }

            const formatBucket = (iso: string) => {
              const d = new Date(iso);
              if (series.bucket === "hour") {
                return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric" });
              }
              return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
            };
            const formatValue = (v: number) =>
              trendMetric === "cost" ? formatUsd(v) : formatNum(v);

            return (
              <Card data-testid="card-gemini-trend">
                <div className="p-4 border-b flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h4 className="font-semibold">Cost trend by user</h4>
                    <p className="text-xs text-muted-foreground">
                      Stacked {trendMetric === "cost" ? "estimated cost" : "total tokens"} per
                      {" "}{series.bucket}, top {trendTopN} users{hasOther ? " plus Other" : ""}.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-muted-foreground">Metric</Label>
                    <Select value={trendMetric} onValueChange={(v) => setTrendMetric(v as "cost" | "totalTokens")}>
                      <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="select-trend-metric">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cost">Est. cost</SelectItem>
                        <SelectItem value="totalTokens">Total tokens</SelectItem>
                      </SelectContent>
                    </Select>
                    <Label className="text-xs text-muted-foreground">Top</Label>
                    <Select value={String(trendTopN)} onValueChange={(v) => setTrendTopN(Number(v))}>
                      <SelectTrigger className="w-[70px] h-8 text-xs" data-testid="select-trend-top-n">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">3</SelectItem>
                        <SelectItem value="5">5</SelectItem>
                        <SelectItem value="10">10</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="p-4">
                  <div className="h-72 w-full" data-testid="chart-gemini-trend">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis
                          dataKey="bucketStart"
                          tickFormatter={(v) => formatBucket(String(v))}
                          minTickGap={24}
                          fontSize={11}
                        />
                        <YAxis
                          tickFormatter={(v) => formatValue(Number(v))}
                          fontSize={11}
                          width={70}
                        />
                        <RechartsTooltip
                          labelFormatter={(label) => formatBucket(String(label))}
                          formatter={(value, name) => [formatValue(Number(value)), String(name)]}
                          contentStyle={{ fontSize: 12 }}
                        />
                        <Legend
                          wrapperStyle={{ fontSize: 11 }}
                          onClick={(entry: any) => handleSeriesClick(String(entry?.dataKey ?? ""))}
                        />
                        {seriesKeys.map((sk) => (
                          <Area
                            key={sk.key}
                            type="monotone"
                            dataKey={sk.key}
                            name={sk.label}
                            stackId="1"
                            stroke={sk.color}
                            fill={sk.color}
                            fillOpacity={0.5}
                            style={sk.key === "__other__" ? undefined : { cursor: "pointer" }}
                            onClick={() => handleSeriesClick(sk.key)}
                          />
                        ))}
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </Card>
            );
          })()}

          {featureFilter && (
            <div
              className="flex items-center justify-between gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs"
              data-testid="banner-gemini-feature-filter"
            >
              <div>
                Showing usage for feature{" "}
                <Badge variant="secondary" data-testid="badge-gemini-feature-filter">{featureFilter}</Badge>
                {" "}only. Totals, top users, recent calls, and the trend chart are filtered.
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setFeatureFilter(null)}
                data-testid="button-clear-gemini-feature-banner"
              >
                Show all features
              </Button>
            </div>
          )}

          <Card>
            <div className="p-4 border-b">
              <h4 className="font-semibold">Usage by feature</h4>
              <p className="text-xs text-muted-foreground">Grouped by feature and model. Click a row to filter the panel by that feature.</p>
            </div>
            {usageQuery.data!.byFeature.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground" data-testid="text-gemini-no-usage">
                No Gemini calls in this window yet.
              </div>
            ) : (() => {
              const total = usageQuery.data!.byFeature.length;
              const safePage = clampPage(usageByFeaturePage, total, usageByFeaturePageSize);
              const start = (safePage - 1) * usageByFeaturePageSize;
              const pageRows = usageQuery.data!.byFeature.slice(start, start + usageByFeaturePageSize);
              return (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Feature</TableHead>
                        <TableHead>Model</TableHead>
                        <TableHead className="text-right">Calls</TableHead>
                        <TableHead className="text-right">Failures</TableHead>
                        <TableHead className="text-right">Input tokens</TableHead>
                        <TableHead className="text-right">Output tokens</TableHead>
                        <TableHead className="text-right">Avg latency</TableHead>
                        <TableHead className="text-right">Est. cost</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pageRows.map((row, idx) => {
                        const absoluteIdx = start + idx;
                        return (
                          <TableRow
                            key={`${row.feature}-${row.model}`}
                            data-testid={`row-gemini-feature-${absoluteIdx}`}
                            className="cursor-pointer hover-elevate"
                            onClick={() => {
                              const label = row.feature ?? "(unknown)";
                              setFeatureFilter((prev) => (prev === label ? null : label));
                            }}
                            title="Click to filter the panel by this feature"
                          >
                            <TableCell className="font-medium" data-testid={`text-gemini-feature-${absoluteIdx}`}>{row.feature ?? "(unknown)"}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{row.model}</TableCell>
                            <TableCell className="text-right">{formatNum(row.calls)}</TableCell>
                            <TableCell className="text-right">
                              {row.failures > 0
                                ? <Badge variant="destructive">{formatNum(row.failures)}</Badge>
                                : <span className="text-muted-foreground">0</span>}
                            </TableCell>
                            <TableCell className="text-right">{formatNum(row.inputTokens)}</TableCell>
                            <TableCell className="text-right">{formatNum(row.outputTokens)}</TableCell>
                            <TableCell className="text-right text-xs text-muted-foreground">{formatNum(row.avgLatencyMs)} ms</TableCell>
                            <TableCell className="text-right font-medium" data-testid={`text-gemini-cost-${absoluteIdx}`}>
                              {formatUsd(row.estimatedCostUsd)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <div className="p-3 border-t">
                    <PaginationControls
                      page={safePage}
                      pageSize={usageByFeaturePageSize}
                      total={total}
                      onPageChange={setUsageByFeaturePage}
                      onPageSizeChange={setUsageByFeaturePageSize}
                      testIdPrefix="gemini-by-feature"
                      itemLabel="features"
                    />
                  </div>
                </>
              );
            })()}
          </Card>

          <Card>
            <div className="p-4 border-b flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className="font-semibold">Top users</h4>
                <p className="text-xs text-muted-foreground">Ranked by consumption within the selected time range.</p>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground">Rank by</Label>
                <Select value={topNMetric} onValueChange={(v) => setTopNMetric(v as "cost" | "totalTokens" | "calls")}>
                  <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="select-top-users-metric">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cost">Est. cost</SelectItem>
                    <SelectItem value="totalTokens">Total tokens</SelectItem>
                    <SelectItem value="calls">Call count</SelectItem>
                  </SelectContent>
                </Select>
                <Label className="text-xs text-muted-foreground">Show top</Label>
                <Select value={String(topN)} onValueChange={(v) => setTopN(Number(v))}>
                  <SelectTrigger className="w-[70px] h-8 text-xs" data-testid="select-top-users-limit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {(() => {
              const sorted = [...(usageQuery.data!.byUser ?? [])].sort((a, b) => {
                if (topNMetric === "cost") return b.estimatedCostUsd - a.estimatedCostUsd;
                if (topNMetric === "totalTokens") return b.totalTokens - a.totalTokens;
                return b.calls - a.calls;
              }).slice(0, topN);
              if (sorted.length === 0) {
                return (
                  <div className="p-6 text-sm text-muted-foreground" data-testid="text-top-users-empty">
                    No usage data in this window.
                  </div>
                );
              }
              const total = sorted.length;
              const safePage = clampPage(topUsersPage, total, topUsersPageSize);
              const start = (safePage - 1) * topUsersPageSize;
              const pageRows = sorted.slice(start, start + topUsersPageSize);
              return (
                <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8">#</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead className="text-right">Calls</TableHead>
                        <TableHead className="text-right">Failures</TableHead>
                        <TableHead className="text-right">Input tokens</TableHead>
                        <TableHead className="text-right">Output tokens</TableHead>
                        <TableHead className="text-right">Total tokens</TableHead>
                        <TableHead className="text-right">Est. cost</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pageRows.map((row, idx) => {
                        const absoluteIdx = start + idx;
                        return (
                        <TableRow
                          key={row.userId ?? "anon"}
                          data-testid={`row-top-user-${absoluteIdx}`}
                          className="cursor-pointer hover-elevate"
                          onClick={() => { setDrillUser({ userId: row.userId, email: row.email, displayName: row.displayName }); setDrillAlertDate(null); }}
                          title="Click to drill into this user's calls"
                        >
                          <TableCell className="text-muted-foreground text-xs">{absoluteIdx + 1}</TableCell>
                          <TableCell className="text-xs">
                            {row.email ? (
                              <div>
                                <div className="font-medium text-foreground">{row.email}</div>
                                {row.displayName && (
                                  <div className="text-muted-foreground">{row.displayName}</div>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground italic">Anonymous</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">{formatNum(row.calls)}</TableCell>
                          <TableCell className="text-right">
                            {row.failures > 0
                              ? <Badge variant="destructive">{formatNum(row.failures)}</Badge>
                              : <span className="text-muted-foreground">0</span>}
                          </TableCell>
                          <TableCell className="text-right">{formatNum(row.inputTokens)}</TableCell>
                          <TableCell className="text-right">{formatNum(row.outputTokens)}</TableCell>
                          <TableCell className="text-right">{formatNum(row.totalTokens)}</TableCell>
                          <TableCell className="text-right font-medium">{formatUsd(row.estimatedCostUsd)}</TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                <div className="p-3 border-t">
                  <PaginationControls
                    page={safePage}
                    pageSize={topUsersPageSize}
                    total={total}
                    onPageChange={setTopUsersPage}
                    onPageSizeChange={setTopUsersPageSize}
                    testIdPrefix="top-users"
                    itemLabel="users"
                  />
                </div>
                </>
              );
            })()}
          </Card>

          <Card data-testid="card-gemini-cost-alerts">
            <div className="p-4 border-b flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className="font-semibold">
                  Recent spike alerts
                  {alertsQuery.data && (
                    <span className="ml-2 text-xs text-muted-foreground" data-testid="text-gemini-alert-counts">
                      {alertsQuery.data.unacknowledgedCount} unacknowledged · {alertsQuery.data.totalCount} total
                    </span>
                  )}
                </h4>
                <p className="text-xs text-muted-foreground">
                  Last 25 cost-spike alerts emailed to admins from the <code>gemini_cost_alerts</code> log. Click a row to drill into that user.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Switch
                    id="switch-gemini-show-acknowledged"
                    checked={showAcknowledged}
                    onCheckedChange={setShowAcknowledged}
                    data-testid="switch-gemini-show-acknowledged"
                  />
                  <Label htmlFor="switch-gemini-show-acknowledged" className="text-xs">
                    Show acknowledged
                  </Label>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => alertsQuery.refetch()}
                  data-testid="button-refresh-gemini-alerts"
                >
                  Refresh
                </Button>
              </div>
            </div>
            {alertsQuery.isLoading ? (
              <div className="p-4 space-y-2">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : alertsQuery.isError ? (
              <div className="p-6 text-sm text-destructive" data-testid="text-gemini-alerts-error">
                Failed to load spike alerts.
              </div>
            ) : !alertsQuery.data || alertsQuery.data.alerts.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground" data-testid="text-gemini-alerts-empty">
                {showAcknowledged
                  ? "No spike alerts have been recorded."
                  : "No unacknowledged alerts. Toggle \"Show acknowledged\" to view past alerts."}
              </div>
            ) : (() => {
              const alertsTotal = alertsQuery.data.alerts.length;
              const alertsSafePage = clampPage(alertsPage, alertsTotal, alertsPageSize);
              const alertsStart = (alertsSafePage - 1) * alertsPageSize;
              const alertsPageRows = alertsQuery.data.alerts.slice(alertsStart, alertsStart + alertsPageSize);
              return (
              <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sent</TableHead>
                      <TableHead>Alert date</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead className="text-right">Daily cost</TableHead>
                      <TableHead className="text-right">Baseline</TableHead>
                      <TableHead className="text-right">Growth</TableHead>
                      <TableHead>Recipients</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {alertsPageRows.map((row) => {
                      const recipientList = row.recipients.join(", ");
                      const recipientPreview = row.recipients.length <= 2
                        ? recipientList
                        : `${row.recipients.slice(0, 2).join(", ")} +${row.recipients.length - 2}`;
                      const isAck = !!row.acknowledgedAt;
                      const isPending = acknowledgeMutation.isPending && acknowledgeMutation.variables?.id === row.id;
                      return (
                        <TableRow
                          key={row.id}
                          data-testid={`row-gemini-alert-${row.id}`}
                          className={`cursor-pointer hover-elevate ${isAck ? "opacity-60" : ""}`}
                          onClick={() => {
                            setDrillUser({ userId: row.userId, email: row.userEmail, displayName: row.userDisplayName });
                            setDrillAlertDate(row.alertDate);
                          }}
                          title="Click to drill into this user's calls"
                        >
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDate(row.createdAt)}
                          </TableCell>
                          <TableCell className="text-xs whitespace-nowrap" data-testid={`text-gemini-alert-date-${row.id}`}>
                            {row.alertDate}
                          </TableCell>
                          <TableCell className="text-xs">
                            {row.userEmail ? (
                              <div>
                                <div className={`font-medium ${isAck ? "text-muted-foreground" : "text-foreground"}`} data-testid={`text-gemini-alert-user-${row.id}`}>{row.userEmail}</div>
                                {row.userDisplayName && (
                                  <div className="text-muted-foreground">{row.userDisplayName}</div>
                                )}
                              </div>
                            ) : row.userId == null ? (
                              <span className="text-muted-foreground italic">Unattributed</span>
                            ) : (
                              <span className="text-muted-foreground">User #{row.userId}</span>
                            )}
                          </TableCell>
                          <TableCell className={`text-right font-medium ${isAck ? "text-muted-foreground" : ""}`} data-testid={`text-gemini-alert-cost-${row.id}`}>
                            {formatUsd(row.dailyCostUsd)}
                          </TableCell>
                          <TableCell className="text-right text-xs text-muted-foreground">
                            {formatUsd(row.baselineCostUsd)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant={isAck ? "secondary" : "destructive"} data-testid={`badge-gemini-alert-growth-${row.id}`}>
                              {row.growthRatio.toFixed(2)}×
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground" title={recipientList}>
                            {recipientPreview || <span className="italic">—</span>}
                          </TableCell>
                          <TableCell className="text-xs">
                            {isAck ? (
                              <div className="text-muted-foreground" data-testid={`text-gemini-alert-ack-${row.id}`}>
                                <div>Acknowledged {formatDate(row.acknowledgedAt!)}</div>
                                {row.acknowledgedBy && <div className="text-[11px]">by {row.acknowledgedBy}</div>}
                              </div>
                            ) : (
                              <Badge variant="outline">New</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            {isAck ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={isPending}
                                onClick={() => acknowledgeMutation.mutate({ id: row.id, acknowledge: false })}
                                data-testid={`button-gemini-alert-unacknowledge-${row.id}`}
                              >
                                Reopen
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={isPending}
                                onClick={() => acknowledgeMutation.mutate({ id: row.id, acknowledge: true })}
                                data-testid={`button-gemini-alert-acknowledge-${row.id}`}
                              >
                                {isPending ? "…" : "Acknowledge"}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div className="p-3 border-t">
                <PaginationControls
                  page={alertsSafePage}
                  pageSize={alertsPageSize}
                  total={alertsTotal}
                  onPageChange={setAlertsPage}
                  onPageSizeChange={setAlertsPageSize}
                  testIdPrefix="gemini-alerts"
                  itemLabel="alerts"
                />
              </div>
              </>
              );
            })()}
          </Card>

          <Card>
            <div className="p-4 border-b">
              <h4 className="font-semibold">Recent calls</h4>
              <p className="text-xs text-muted-foreground">Most recent 100 calls in the selected window.</p>
            </div>
            {usageQuery.data!.recent.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground">No recent calls.</div>
            ) : (() => {
              const recentTotal = usageQuery.data!.recent.length;
              const recentSafePage = clampPage(usageRecentPage, recentTotal, usageRecentPageSize);
              const recentStart = (recentSafePage - 1) * usageRecentPageSize;
              const recentPageRows = usageQuery.data!.recent.slice(recentStart, recentStart + usageRecentPageSize);
              return (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>When</TableHead>
                          <TableHead>Feature</TableHead>
                          <TableHead>Model</TableHead>
                          <TableHead>User</TableHead>
                          <TableHead className="text-right">Tokens (in/out)</TableHead>
                          <TableHead className="text-right">Latency</TableHead>
                          <TableHead className="text-right">Cost</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {recentPageRows.map((row) => (
                          <TableRow key={row.id} data-testid={`row-gemini-recent-${row.id}`}>
                            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatDate(row.createdAt)}
                            </TableCell>
                            <TableCell>{row.feature}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{row.model}</TableCell>
                            <TableCell className="text-xs">
                              {row.userEmail ? (
                                <div>
                                  <div className="font-medium text-foreground">{row.userEmail}</div>
                                  {row.userDisplayName && (
                                    <div className="text-muted-foreground">{row.userDisplayName}</div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right text-xs whitespace-nowrap">
                              {formatNum(row.inputTokens)} / {formatNum(row.outputTokens)}
                            </TableCell>
                            <TableCell className="text-right text-xs text-muted-foreground">{formatNum(row.latencyMs)} ms</TableCell>
                            <TableCell className="text-right text-xs">{formatUsd(row.estimatedCostUsd)}</TableCell>
                            <TableCell>
                              {row.success
                                ? <Badge variant="secondary">ok</Badge>
                                : <Badge variant="destructive" title={row.errorMessage ?? undefined}>fail</Badge>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="p-3 border-t">
                    <PaginationControls
                      page={recentSafePage}
                      pageSize={usageRecentPageSize}
                      total={recentTotal}
                      onPageChange={setUsageRecentPage}
                      onPageSizeChange={setUsageRecentPageSize}
                      testIdPrefix="gemini-recent"
                      itemLabel="calls"
                    />
                  </div>
                </>
              );
            })()}
          </Card>
        </>
      )}
      <GeminiAlertSettingsCard />
      <GeminiUserDrilldownDialog
        user={drillUser}
        hours={hours}
        rangeLabel={ranges.find((r) => r.value === hours)?.label ?? `Last ${hours} hours`}
        alertDate={drillAlertDate}
        onClose={() => { setDrillUser(null); setDrillAlertDate(null); }}
      />
    </div>
  );
}
